import React from "react";
import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProjects } from "../../../context/ProjectsContext";
import ModuleDisabledState from "../../../components/dashboard/ModuleDisabledState";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import { isProjectCapabilityEnabled } from "../../../utils/projectCapabilities";
import { isProjectModuleEnabled } from "../../../utils/projectModules";
import { hasProjectPermission, normalizeProjectAccess } from "../../../utils/projectAccess";
import { isSuperAdmin } from "../../../utils/roles";

function matchProject(project, key) {
  return project && [project.slug, project.id, project._id].filter(Boolean).some((value) => String(value) === String(key));
}

function domainEnabled(project, capability, moduleKey) {
  return isProjectCapabilityEnabled(project, capability) && isProjectModuleEnabled(project, moduleKey);
}

export default function ProjectRealEstateLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();

  React.useEffect(() => { loadProjects(); }, [loadProjects]);
  const project = React.useMemo(
    () => projects.find((item) => matchProject(item, projectId)) || (matchProject(activeProject, projectId) ? activeProject : null),
    [projects, activeProject, projectId],
  );
  React.useEffect(() => {
    const found = projects.find((item) => matchProject(item, projectId));
    if (found && found !== activeProject) setActiveProject(found);
  }, [projects, projectId, activeProject, setActiveProject]);

  if (!project) return <DashboardLoadingState label="Preparando Inmobiliaria…" minHeight={360} />;

  const access = normalizeProjectAccess(project.memberAccess);
  const superAdmin = isSuperAdmin(user);
  const accessResolved = superAdmin || access.resolved;
  const propertiesEnabled = domainEnabled(project, "properties", "properties");
  const leasesEnabled = domainEnabled(project, "leases", "leases");
  const collectionsEnabled = domainEnabled(project, "leaseCollections", "lease_collections");
  const paymentsEnabled = isProjectModuleEnabled(project, "ecommerce");

  const permission = (name, { manage = false } = {}) => {
    if (superAdmin) return true;
    if (!accessResolved) return !manage;
    return hasProjectPermission(access, name);
  };

  const capabilities = {
    propertiesEnabled,
    leasesEnabled,
    collectionsEnabled,
    canReadProperties: propertiesEnabled && permission("properties.read"),
    canManageProperties: propertiesEnabled && permission("properties.manage", { manage: true }),
    canReadLeases: leasesEnabled && permission("leases.read"),
    canManageLeases: leasesEnabled && permission("leases.manage", { manage: true }),
    canReadCollections: collectionsEnabled && permission("lease_collections.read"),
    canManageCollections: collectionsEnabled && permission("lease_collections.manage", { manage: true }),
    canSendNotifications: collectionsEnabled && permission("notifications.send", { manage: true }),
    canCreatePaymentLinks: collectionsEnabled && paymentsEnabled && permission("lease_collections.manage", { manage: true }),
  };

  const anyEnabled = propertiesEnabled || leasesEnabled || collectionsEnabled;
  const anyReadable = capabilities.canReadProperties || capabilities.canReadLeases || capabilities.canReadCollections;
  if (!anyEnabled) {
    return <ModuleDisabledState projectId={projectId} title="Inmobiliaria" description="Activá Propiedades, Contratos o Cobranzas para usar este espacio." />;
  }
  if (accessResolved && !anyReadable) {
    return <Alert severity="warning">Tu acceso no incluye lectura del módulo inmobiliario. Pedile al titular o a un administrador que revise tus permisos.</Alert>;
  }

  const base = `/dashboard/proyectos/${projectId}/inmobiliaria`;
  const tabs = [
    ["overview", base, <SpaceDashboardRoundedIcon />, "Resumen", true],
    ["properties", `${base}/propiedades`, <HomeWorkRoundedIcon />, "Propiedades", capabilities.canReadProperties],
    ["leases", `${base}/contratos`, <KeyRoundedIcon />, "Contratos", capabilities.canReadLeases],
    ["collections", `${base}/cobranzas`, <PaymentsRoundedIcon />, "Cobranzas", capabilities.canReadCollections],
  ].filter((item) => item[4]);
  const current = location.pathname.endsWith("/propiedades")
    ? "properties"
    : location.pathname.endsWith("/contratos")
      ? "leases"
      : location.pathname.endsWith("/cobranzas")
        ? "collections"
        : "overview";
  const currentAllowed = current === "overview"
    || (current === "properties" && capabilities.canReadProperties)
    || (current === "leases" && capabilities.canReadLeases)
    || (current === "collections" && capabilities.canReadCollections);

  return (
    <Stack spacing={{ xs: 2, md: 3 }} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 950, color: "#19242F", fontSize: { xs: 30, sm: 36, md: 44 }, lineHeight: 1.08 }}>Inmobiliaria</Typography>
        <Typography sx={{ mt: .5, color: "rgba(25,36,47,.65)" }}>{project.name || projectId} · propiedades, contratos y cobranzas en un dominio separado del catálogo comercial.</Typography>
      </Box>
      <Box sx={{ mx: { xs: -1, sm: 0 }, px: { xs: 1, sm: 0 }, overflowX: "auto" }}>
        <Tabs value={current} onChange={(_event, value) => { const tab = tabs.find((item) => item[0] === value); if (tab) navigate(tab[1]); }} variant="scrollable" scrollButtons={false} allowScrollButtonsMobile>
          {tabs.map(([value, , icon, label]) => <Tab key={value} value={value} icon={icon} iconPosition="start" label={label} sx={{ fontWeight: 900, textTransform: "none" }} />)}
        </Tabs>
      </Box>
      {currentAllowed ? (
        <Outlet context={{ project, projectId, ...capabilities }} />
      ) : (
        <Alert severity="warning">Tu acceso no incluye esta sección de Inmobiliaria.</Alert>
      )}
    </Stack>
  );
}
